import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { planKeys, useQuotaQuery } from '@entities/Plan';
import { useAuthStore } from '@entities/User';
import {
  checkSocialReward,
  fetchSocialReward,
  fetchTelegramLinks,
  fetchVkChatLink,
  socialRewardErrorCodeKey,
  socialRewardErrorKey,
  useSocialRewardReset,
  type SocialRewardStatus,
} from '../api/socialRewardApi';

interface Feedback {
  tone: 'error' | 'info';
  key: string;
}

// The bots confirm a subscription on their own; poll briefly after the user leaves for Telegram/VK.
const WATCH_INTERVAL_MS = 3_000;
const WATCH_DURATION_MS = 3 * 60_000;
// A used gift says "thanks" for a short while, then gets out of the way.
const USED_CARD_MS = 3 * 24 * 60 * 60_000;

// Only a UI preference: it never decides eligibility.
function readHidden(key: string | null): boolean {
  if (!key) return false;
  try {
    return localStorage.getItem(key) === '1';
  } catch {
    return false;
  }
}

export function useSocialReward() {
  const userId = useAuthStore((state) => state.user?.id);
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [open, setOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [redeemed, setRedeemed] = useState(false);
  const [feedback, setFeedback] = useState<Feedback | null>(null);
  const [watchUntil, setWatchUntil] = useState(0);
  const [opened, setOpened] = useState({ telegram: false, vk: false });
  const recheckVkOnReturn = useRef(false);
  const queryKey = ['social-reward', userId] as const;
  const hiddenKey = userId ? `lomora.social-reward-hidden.${userId}` : null;
  const [hidden, setHidden] = useState(() => readHidden(hiddenKey));
  useEffect(() => setHidden(readHidden(hiddenKey)), [hiddenKey]);

  const statusQuery = useQuery({
    queryKey,
    queryFn: fetchSocialReward,
    enabled: Boolean(userId),
    staleTime: 30_000,
    refetchOnWindowFocus: open,
    refetchInterval: (query) =>
      open && query.state.data?.enabled && Date.now() < watchUntil ? WATCH_INTERVAL_MS : false,
  });
  const status = statusQuery.data;
  // Links stay useful after the gift is earned: every subscription counts.
  const listed = Boolean(status?.enabled && status.rewardStatus !== 'unavailable');
  const telegramLinksQuery = useQuery({
    queryKey: ['social-reward', 'telegram-links', userId] as const,
    queryFn: fetchTelegramLinks,
    // Links are real <a href>: tg:// must open from the click itself, not after an await.
    enabled: open && listed && status?.telegram.subscribed !== true,
    staleTime: 10 * 60_000,
    retry: 1,
    refetchOnWindowFocus: false,
  });
  const vkLinkQuery = useQuery({
    queryKey: ['social-reward', 'vk-link', userId] as const,
    queryFn: fetchVkChatLink,
    enabled: open && listed && status?.vk.subscribed !== true,
    staleTime: 10 * 60_000,
    retry: 1,
    refetchOnWindowFocus: false,
  });
  const quotaQuery = useQuotaQuery({ enabled: open && status?.rewardStatus === 'available' });
  const { refetch } = statusQuery;
  const callbackResult = searchParams.get('social_reward');
  const callbackErrorCode = searchParams.get('social_reward_error');

  useEffect(() => {
    if (callbackResult !== 'connected' && callbackResult !== 'error') return;
    setOpen(true);
    // On success the VK row itself shows the checked membership.
    setFeedback(
      callbackResult === 'connected'
        ? null
        : { tone: 'error', key: socialRewardErrorCodeKey(callbackErrorCode, 'socialReward.linkError') },
    );
    void refetch();
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.delete('social_reward');
        next.delete('social_reward_error');
        return next;
      },
      { replace: true },
    );
  }, [callbackResult, callbackErrorCode, refetch, setSearchParams]);

  const saveStatus = (next: SocialRewardStatus) => queryClient.setQueryData(queryKey, next);
  const onError = (error: unknown, fallback: string) =>
    setFeedback({ tone: 'error', key: socialRewardErrorKey(error, fallback) });

  const checkMutation = useMutation({
    mutationFn: checkSocialReward,
    onSuccess: saveStatus,
    onError: (error) => onError(error, 'socialReward.verifyError'),
  });
  const useMutationResult = useMutation({
    mutationFn: useSocialRewardReset,
    onSuccess: async (next) => {
      saveStatus(next);
      setConfirming(false);
      setRedeemed(next.rewardStatus === 'used');
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: planKeys.quota }),
        queryClient.invalidateQueries({ queryKey: planKeys.billing }),
      ]);
    },
    onError: (error) => {
      onError(error, 'socialReward.redeemError');
      // A timeout may have happened after the server committed the reset.
      // Reconcile its durable result before offering another attempt.
      void queryClient.invalidateQueries({ queryKey });
      void queryClient.invalidateQueries({ queryKey: planKeys.quota });
      void queryClient.invalidateQueries({ queryKey: planKeys.billing });
    },
  });

  const busy = checkMutation.isPending || useMutationResult.isPending;
  const usedAt = status?.rewardStatus === 'used' && status.usedAt ? Date.parse(status.usedAt) : null;
  const cardHidden = usedAt !== null && (hidden || Date.now() - usedAt > USED_CARD_MS);
  const runCheck = checkMutation.mutate;
  const check = useCallback(() => {
    if (busy) return;
    setFeedback(null);
    runCheck();
  }, [busy, runCheck]);

  useEffect(() => {
    // A linked VK account is re-checked when the user comes back from subscribing.
    if (!open) return;
    const onFocus = () => {
      if (!recheckVkOnReturn.current) return;
      recheckVkOnReturn.current = false;
      check();
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [open, check]);

  const close = useCallback(() => {
    setOpen(false);
    setConfirming(false);
    setFeedback(null);
    setWatchUntil(0);
    setOpened({ telegram: false, vk: false });
    recheckVkOnReturn.current = false;
  }, []);

  return {
    statusQuery,
    telegramLinksQuery,
    vkLinkQuery,
    quotaQuery,
    open,
    confirming,
    redeemed,
    feedback,
    busy,
    opened,
    cardHidden,
    hideCard: () => {
      try {
        if (hiddenKey) localStorage.setItem(hiddenKey, '1');
      } catch {
        // Storage may be blocked; hiding still works for this visit.
      }
      setHidden(true);
    },
    checking: checkMutation.isPending,
    using: useMutationResult.isPending,
    show: () => {
      setConfirming(false);
      setFeedback(null);
      setOpen(true);
      void refetch();
    },
    close,
    confirm: () => {
      setFeedback(null);
      setConfirming(true);
      void refetch();
      void quotaQuery.refetch();
    },
    cancelConfirm: () => setConfirming(false),
    /** The user went to Telegram/VK: the bot confirms on its own, keep the status fresh meanwhile. */
    watch: (network: 'telegram' | 'vk') => {
      setFeedback(null);
      setOpened((current) => ({ ...current, [network]: true }));
      setWatchUntil(Date.now() + WATCH_DURATION_MS);
    },
    /** Subscribe link clicked for an already linked VK account. */
    recheckVkOnReturn: () => {
      recheckVkOnReturn.current = true;
    },
    check,
    useReset: () => {
      if (busy || statusQuery.isFetching) return;
      setFeedback(null);
      useMutationResult.mutate();
    },
  };
}

export type SocialRewardController = ReturnType<typeof useSocialReward>;
